import { useGetSubscriptionSummary } from "@/services/queries/subscription";
import { useTranslation } from "react-i18next";
import { Spinner } from "@phosphor-icons/react";

const SubscriptionInfo = () => {
  const { t } = useTranslation();
  const { data, isLoading, isError } = useGetSubscriptionSummary();

  if (isLoading) {
    return (
      <div
        className="flex items-center justify-center px-5 py-4"
        data-testid="subscription-loading"
      >
        <Spinner size={20} className="animate-spin text-white" />
      </div>
    );
  }

  if (isError) {
    return null;
  }

  const summary = data?.value;
  if (!summary) return null;

  const isExpired = summary.status === "expired";
  const isUnlimited = summary.vesselLimit === 0;

  const vesselUsage = isUnlimited
    ? `${summary.vesselCount} / ${t("subscription.unlimited")}`
    : `${summary.vesselCount} / ${summary.vesselLimit}`;

  const formattedEndDate = summary.trialEndsAt
    ? new Date(summary.trialEndsAt).toLocaleDateString()
    : null;

  return (
    <div className="px-5 py-4" data-testid="subscription-info">
      <span className="text-xs font-semibold uppercase tracking-wider text-white/50">
        {t("subscription.title")}
      </span>

      <div className="mt-3 space-y-2">
        <div className="flex items-center justify-between">
          <span className="text-sm text-white/70">
            {t("subscription.plan")}
          </span>
          <span
            className="text-sm font-medium text-white"
            data-testid="subscription-plan"
          >
            {summary.plan}
          </span>
        </div>

        <div className="flex items-center justify-between">
          <span className="text-sm text-white/70">{t("label.status")}</span>
          <span
            className={`text-sm font-medium ${isExpired ? "text-[#E86363]" : "text-[#4ADE80]"}`}
            data-testid="subscription-status"
          >
            {isExpired ? t("subscription.expired") : t("subscription.active")}
          </span>
        </div>

        {summary.daysRemaining != null && !isExpired && (
          <div className="flex items-center justify-between">
            <span className="text-sm text-white/70">
              {t("subscription.days_remaining")}
            </span>
            <span
              className="text-sm font-medium text-white"
              data-testid="subscription-days"
            >
              {t("subscription.days_count", { count: summary.daysRemaining })}
            </span>
          </div>
        )}

        {formattedEndDate && (
          <div className="flex items-center justify-between">
            <span className="text-sm text-white/70">
              {t("subscription.end_date")}
            </span>
            <span
              className="text-sm font-medium text-white"
              data-testid="subscription-end-date"
            >
              {formattedEndDate}
            </span>
          </div>
        )}

        <div className="flex items-center justify-between">
          <span className="text-sm text-white/70">
            {t("subscription.vessel_usage")}
          </span>
          <span
            className="text-sm font-medium text-white"
            data-testid="subscription-vessel-usage"
          >
            {vesselUsage}
          </span>
        </div>

        {isExpired && (
          <p
            className="mt-2 text-xs text-[#E86363]/80"
            data-testid="subscription-expired-message"
          >
            {t("subscription.expired_message")}
          </p>
        )}

        {!isUnlimited && !summary.canAddVessel && !isExpired && (
          <p
            className="mt-2 text-xs text-[#FBBF24]/80"
            data-testid="subscription-vessel-limit-message"
          >
            {t("subscription.vessel_limit_reached")}
          </p>
        )}
      </div>
    </div>
  );
};

export default SubscriptionInfo;
